import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { debounce, groupBy, orderBy, upperCase } from 'lodash';

import { ClinicUserRole, useUser } from 'context/userContext';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as CancelIcon } from 'assets/icons/cancel.svg';
import { ReactComponent as DropdownArrowIcon } from 'assets/icons/arrow-down.svg';
import { highlightText } from 'utils/common';
import CommonCheckBox from 'components/CommonCheckBox/CommonCheckBox';
import { OTHER_GROUP_NAME } from 'utils/constants';

interface IOption extends ClinicUserRole {
  id: string;
  name: string;
}

interface IProps {
  onSelect: (clinicIds: string[]) => void;
}

const ALL_OPTION = {
  id: null,
  name: 'All Practices',
};

const groupByPath = (item: ClinicUserRole) => item.clinic.clinicGroup?.name;

const ClinicFilter = ({ onSelect }: IProps) => {
  const {
    user: { clinicUserRoles },
  } = useUser();

  const initialOptions = useMemo(() => {
    return clinicUserRoles.reduce((accum: IOption[], currentValue) => {
      if (currentValue.clinic) {
        accum.push({
          ...currentValue,
          id: currentValue.clinic.id,
          name: currentValue.clinic.name,
        });
      }

      return accum;
    }, []);
  }, [clinicUserRoles]);

  const [searchingValue, setSearchingValue] = useState('');
  const [searchingClinics, setSearchingClinics] = useState(initialOptions);
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>(() => {
    return initialOptions.map((item) => item.id);
  });

  const [isSelectedAll, setIsSelectedAll] = useState(true);

  const clinicCheckRef = useRef<HTMLInputElement | null>(null);

  const debounceFn = debounce((value) => {
    const regex = new RegExp(value, 'gi');

    let searchingResults = initialOptions.filter((item) => {
      return item.name.search(regex) !== -1;
    });

    if (searchingResults.length === 0) {
      searchingResults = initialOptions.filter((item) => {
        return upperCase(item.clinic.clinicGroup?.name).search(regex) !== -1;
      });
    }

    setSearchingClinics(searchingResults);
  }, 100);

  const searchClinic = (event: any) => {
    const value = event.target.value;
    setSearchingValue(value);
    debounceFn(value);
  };

  const clearSearchClinic = () => {
    setSearchingValue('');
    debounceFn('');
  };

  const onSelectOption = (selectedItems: string[]) => {
    const originalClinicIds = initialOptions.map((item) => item.id);

    const isAllClinicsSelected =
      selectedItems.length === originalClinicIds.length;

    if (isAllClinicsSelected) {
      setIsSelectedAll(true);
      if (clinicCheckRef.current) {
        clinicCheckRef.current.indeterminate = false;
      }
    } else {
      setIsSelectedAll(false);

      if (selectedItems.length !== 0 && clinicCheckRef.current) {
        clinicCheckRef.current.indeterminate = true;
      }

      if (selectedItems.length === 0 && clinicCheckRef.current) {
        clinicCheckRef.current.indeterminate = false;
      }
    }

    setSelectedClinicIds(selectedItems);
  };

  const onSelectAllOption = (event: any) => {
    const isChecked = event.target.checked;
    const searchClinicIds = searchingClinics.map((item) => item.id);

    if (isChecked) {
      setSelectedClinicIds(searchClinicIds);
    } else {
      setSelectedClinicIds([]);
    }

    setIsSelectedAll(event.target.checked);
  };

  const generateClinicName = () => {
    const name = initialOptions
      .filter((item) => selectedClinicIds.includes(item.id))
      .map((item) => item.name);

    return name.join(', ') || 'No Selected Practices';
  };

  useEffect(() => {
    onSelect(selectedClinicIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinicIds]);

  const groupByClinicGroupName = groupBy(
    orderBy(
      searchingClinics,
      [(item) => groupByPath(item) === OTHER_GROUP_NAME, groupByPath],
      ['asc', 'asc']
    ),
    groupByPath
  );

  const onGroupChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    clinics: ClinicUserRole[]
  ) => {
    const isChecked = e.target.checked;
    const clinicIds = clinics.map((item) => item.clinic.id);

    let updatedSelectedClinicIds = [...selectedClinicIds];

    if (!isChecked) {
      updatedSelectedClinicIds = updatedSelectedClinicIds.filter(
        (id) => !clinicIds.includes(id)
      );
    } else {
      updatedSelectedClinicIds = clinics.reduce((accum, currentValue) => {
        if (!updatedSelectedClinicIds.includes(currentValue.clinic.id)) {
          accum.push(currentValue.clinic.id);
        }
        return accum;
      }, updatedSelectedClinicIds);
    }

    if (updatedSelectedClinicIds.length === initialOptions.length) {
      setIsSelectedAll(true);
      clinicCheckRef.current!.indeterminate = false;
      clinicCheckRef.current!.checked = true;
    } else {
      setIsSelectedAll(false);

      if (updatedSelectedClinicIds.length !== 0) {
        clinicCheckRef.current!.indeterminate = true;
      } else {
        clinicCheckRef.current!.indeterminate = false;
      }
      clinicCheckRef.current!.checked = false;
    }

    setSelectedClinicIds(updatedSelectedClinicIds);
  };

  return (
    <Listbox
      value={selectedClinicIds}
      onChange={onSelectOption}
      as="div"
      className="relative"
      multiple
    >
      <Listbox.Button
        className={`text-bold flex flex-row justify-between items-center w-[22rem] h-[4.8rem] py-1.3 px-1.6 rounded-[1rem] shadow-primary hover:shadow-elevation-10 border border-transparent hover:border-magenta`}
      >
        <span className="font-bold truncate basis-[80%] text-left">
          {generateClinicName()}
        </span>
        <DropdownArrowIcon width={14} height={14} />
      </Listbox.Button>
      <Listbox.Options
        className={`overflow-hidden top-6 absolute z-[9999] w-[25.5rem] bg-white rounded-[1rem] flex flex-col py-1.6 px-2 shadow-primary ${
          searchingClinics.length > 5 ? 'h-[27.5rem]' : 'h-auto'
        }`}
      >
        <div className="flex flex-row mb-1.6">
          <SearchIcon className="mr-[1rem]" width={20} />
          <input
            value={searchingValue}
            placeholder="Search ..."
            className="text-14 placeholder-mid-grey focus:outline-none focus-visible:outline-none w-full"
            onChange={searchClinic}
            onKeyDown={(e) => e.stopPropagation()}
          />
          {searchingValue && (
            <CancelIcon
              className="ml-[1rem] cursor-pointer"
              width={20}
              onClick={clearSearchClinic}
            />
          )}
        </div>

        <div className="overflow-auto overscroll-contain scrollbar h-full flex flex-col items-start">
          <div
            className={`mb-1.6 hover:text-magenta w-full text-left min-h-[2.7rem] truncate cursor-pointer ${
              searchingValue ? 'hidden' : 'block'
            }`}
          >
            <CommonCheckBox
              id={ALL_OPTION.name}
              isChecked={isSelectedAll}
              ref={clinicCheckRef}
              onChange={onSelectAllOption}
              disabled={searchingValue.length !== 0}
              indeterminate={clinicCheckRef?.current?.indeterminate}
            >
              <label htmlFor={ALL_OPTION.name} className={'text-14'}>
                {ALL_OPTION.name}
              </label>
            </CommonCheckBox>
          </div>

          {Object.entries(groupByClinicGroupName).map(
            ([subdomain, clinics]) => {
              const subdomainName = subdomain.toUpperCase();

              const highlightedParts = highlightText(
                subdomainName,
                searchingValue
              );

              const isAllClinicsSelected = clinics.every((item) =>
                selectedClinicIds.some((clinicId) => item.id === clinicId)
              );

              const isSomeClinisSelected = clinics.some((item) =>
                selectedClinicIds.some((clinicId) => item.id === clinicId)
              );

              return (
                <div key={subdomain} className="w-full">
                  {subdomain && (
                    <div className="flex items-center mb-1.6 truncate w-full cursor-pointer">
                      <CommonCheckBox
                        id={subdomain}
                        isChecked={isAllClinicsSelected}
                        ref={(element) => {
                          if (element) {
                            element.indeterminate =
                              !isAllClinicsSelected && isSomeClinisSelected;
                          }
                        }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onGroupChange(e, clinics)
                        }
                      >
                        <label htmlFor={subdomain}>
                          {highlightedParts.map((part, index) => (
                            <span
                              key={index}
                              className={
                                part.highlight
                                  ? 'text-magenta text-12 font-semibold'
                                  : 'text-mid-grey text-12 font-semibold'
                              }
                            >
                              {part.text}
                            </span>
                          ))}
                        </label>
                      </CommonCheckBox>
                    </div>
                  )}
                  {clinics.map((item) => {
                    const isSelected = selectedClinicIds.includes(item.id);

                    const highlightedParts = highlightText(
                      item.name,
                      searchingValue
                    );

                    return (
                      <Listbox.Option
                        value={item.id}
                        key={item.id}
                        as="button"
                        className={`mb-1.6 hover:text-magenta w-full text-left min-h-[2.7rem] truncate pl-1`}
                      >
                        <CommonCheckBox isChecked={isSelected}>
                          <div>
                            {highlightedParts.map((part, index) => (
                              <span
                                key={index}
                                className={
                                  part.highlight
                                    ? 'text-magenta text-14'
                                    : 'text-14'
                                }
                              >
                                {part.text}
                              </span>
                            ))}
                          </div>
                        </CommonCheckBox>
                      </Listbox.Option>
                    );
                  })}
                </div>
              );
            }
          )}
        </div>
      </Listbox.Options>
    </Listbox>
  );
};

export default ClinicFilter;
